import { useState, useEffect, useCallback, useRef } from 'react';
import { Moon, MapPin, Clock, Sunrise, Sunset, ChevronDown, BookOpen, Info, X } from 'lucide-react';
import { 
  calculatePrayerTimes,
  calculatePrayerTimesSync,
  formatPrayerTime, 
  getCountdown, 
  getUserLocation,
  POPULAR_CITIES,
  isLaylatulQadr,
  isOddNight
} from '@/utils/prayerTimes';

// Translations for Ramadan features
const RAMADAN_TRANSLATIONS = {
  de: {
    iftarIn: 'Maghrib in',
    iftarNow: 'Maghrib Zeit!',
    fajrUntil: 'Fajr in',
    fajrEnded: 'Fajr begonnen',
    selectCity: 'Stadt wählen',
    detectLocation: 'Standort erkennen',
    locationDetected: 'Standort erkannt',
    imsak: 'Imsak',
    fajr: 'Fajr',
    maghrib: 'Maghrib',
    laylatulQadr: 'Laylatul Qadr',
    oddNight: 'Ungerade Nacht',
    blessedNight: 'Gesegnete Nacht',
    hours: 'Std',
    minutes: 'Min',
    seconds: 'Sek',
    ramadanDay: 'Ramadan Tag',
    lastTenNights: 'Letzte 10 Nächte',
    searchForNight: 'Suche nach Laylatul Qadr',
    quranReminder: 'Verbringe heute Zeit mit dem Quran',
    quranSource: 'Lies, rezitiere, reflektiere',
    ramadanInfo: 'Ramadan Info',
    imsakTitle: 'IMSAK & RAMADAN',
    imsakInfo: 'Das Imsak ist die Zeit, zu der viele Muslime vorsichtshalber mit dem Essen aufhören (ca. 10 Min. vor Fajr). Es gibt unterschiedliche Gelehrtenmeinungen: Während einige Imsak als Sicherheitsgrenze nutzen, erlauben andere das Essen bis zum tatsächlichen Beginn des Fajr-Gebets. Während des Ramadan ist diese Zeit für Suhoor entscheidend.',
    iftarDuaTitle: 'DUA ZUM IFTAR',
    iftarDuaArabic: 'اللَّهُمَّ لَكَ صُمْتُ وَعَلَى رِزْقِكَ أَفْطَرْتُ',
    iftarDuaTranslit: 'Allahumma laka sumtu wa \'ala rizqika aftartu',
    iftarDuaInfo: 'Dieses Bittgebet (Dua) wird beim Fastenbrechen (Iftar) gesprochen. Wir danken Allah für Seine Segnungen und die Kraft, die uns verliehen wurde, um das Fasten zu vollenden.'
  },
  en: {
    iftarIn: 'Maghrib in',
    iftarNow: 'Maghrib Time!',
    fajrUntil: 'Fajr in',
    fajrEnded: 'Fajr started',
    selectCity: 'Select city',
    detectLocation: 'Detect location',
    locationDetected: 'Location detected',
    imsak: 'Imsak',
    fajr: 'Fajr',
    maghrib: 'Maghrib',
    laylatulQadr: 'Laylatul Qadr',
    oddNight: 'Odd Night',
    blessedNight: 'Blessed Night',
    hours: 'hrs',
    minutes: 'min',
    seconds: 'sec',
    ramadanDay: 'Ramadan Day',
    lastTenNights: 'Last 10 Nights',
    searchForNight: 'Search for Laylatul Qadr',
    quranReminder: 'Spend time with the Quran today',
    quranSource: 'Read, recite, reflect',
    ramadanInfo: 'Ramadan Info',
    imsakTitle: 'IMSAK & RAMADAN',
    imsakInfo: 'Imsak is the time when many Muslims stop eating as a precaution (approx. 10 min. before Fajr). There are different scholarly opinions: While some use Imsak as a safety margin, others allow eating until the actual start of Fajr prayer. During Ramadan, this time is crucial for Suhoor.',
    iftarDuaTitle: 'DUA FOR IFTAR',
    iftarDuaArabic: 'اللَّهُمَّ لَكَ صُمْتُ وَعَلَى رِزْقِكَ أَفْطَرْتُ',
    iftarDuaTranslit: 'Allahumma laka sumtu wa \'ala rizqika aftartu',
    iftarDuaInfo: 'This supplication (Dua) is recited when breaking the fast (Iftar). We thank Allah for His blessings and the strength He granted us to complete the fast.'
  },
  bs: {
    iftarIn: 'Akšam za',
    iftarNow: 'Vrijeme akšama!',
    fajrUntil: 'Sabah za',
    fajrEnded: 'Sabah je nastupio',
    selectCity: 'Odaberi grad',
    detectLocation: 'Otkrij lokaciju',
    locationDetected: 'Lokacija otkrivena',
    imsak: 'Imsak',
    fajr: 'Sabah',
    maghrib: 'Akšam',
    laylatulQadr: 'Lejletul-kadr',
    oddNight: 'Neparna noć',
    blessedNight: 'Blagoslovljena noć',
    hours: 'sati',
    minutes: 'min',
    seconds: 'sek',
    ramadanDay: 'Ramazanski dan',
    lastTenNights: 'Zadnjih 10 noći',
    searchForNight: 'Traži Lejletul-kadr',
    quranReminder: 'Provedi danas vrijeme s Kur\'anom',
    quranSource: 'Čitaj, uči, razmišljaj',
    ramadanInfo: 'Ramazan Info',
    imsakTitle: 'IMSAK & RAMAZAN',
    imsakInfo: 'Imsak je vrijeme kada mnogi muslimani iz predostrožnosti prestaju jesti (oko 10 min. prije sabaha). Postoje različita mišljenja učenjaka: Dok neki koriste imsak kao sigurnosnu granicu, drugi dozvoljavaju jelo do stvarnog početka sabah-namaza. Tokom Ramazana, ovo vrijeme je ključno za sehur.',
    iftarDuaTitle: 'DOVA ZA IFTAR',
    iftarDuaArabic: 'اللَّهُمَّ لَكَ صُمْتُ وَعَلَى رِزْقِكَ أَفْطَرْتُ',
    iftarDuaTranslit: 'Allahumme leke sumtu ve \'ala rizkike eftartu',
    iftarDuaInfo: 'Ova dova se uči prilikom iftara. Zahvaljujemo Allahu na Njegovim blagodatima i snazi koju nam je dao da upotpunimo post.'
  },
  ar: {
    iftarIn: 'المغرب في',
    iftarNow: 'وقت المغرب!',
    fajrUntil: 'الفجر في',
    fajrEnded: 'بدأ الفجر',
    selectCity: 'اختر المدينة',
    detectLocation: 'اكتشف الموقع',
    locationDetected: 'تم اكتشاف الموقع',
    imsak: 'الإمساك',
    fajr: 'الفجر',
    maghrib: 'المغرب',
    laylatulQadr: 'ليلة القدر',
    oddNight: 'ليلة وترية',
    blessedNight: 'ليلة مباركة',
    hours: 'ساعات',
    minutes: 'دقيقة',
    seconds: 'ثانية',
    ramadanDay: 'يوم رمضان',
    lastTenNights: 'العشر الأواخر',
    searchForNight: 'ابحث عن ليلة القدر',
    quranReminder: 'اقضِ وقتاً مع القرآن اليوم',
    quranSource: 'اقرأ، رتّل، تدبّر',
    ramadanInfo: 'معلومات رمضان',
    imsakTitle: 'الإمساك ورمضان',
    imsakInfo: 'الإمساك هو الوقت الذي يتوقف فيه كثير من المسلمين عن الأكل احتياطاً (حوالي 10 دقائق قبل الفجر). هناك آراء فقهية مختلفة: بينما يستخدم البعض الإمساك كهامش أمان، يسمح آخرون بالأكل حتى بداية صلاة الفجر الفعلية.',
    iftarDuaTitle: 'دعاء الإفطار',
    iftarDuaArabic: 'اللَّهُمَّ لَكَ صُمْتُ وَعَلَى رِزْقِكَ أَفْطَرْتُ',
    iftarDuaTranslit: 'اللهم لك صمت وعلى رزقك أفطرت',
    iftarDuaInfo: 'هذا الدعاء يُقال عند الإفطار. نشكر الله على نعمه والقوة التي منحنا إياها لإتمام الصيام.'
  },
  id: {
    iftarIn: 'Maghrib dalam',
    iftarNow: 'Waktu Maghrib!',
    fajrUntil: 'Subuh dalam',
    fajrEnded: 'Subuh dimulai',
    selectCity: 'Pilih kota',
    detectLocation: 'Deteksi lokasi',
    locationDetected: 'Lokasi terdeteksi',
    imsak: 'Imsak',
    fajr: 'Subuh',
    maghrib: 'Maghrib',
    laylatulQadr: 'Lailatul Qadr',
    oddNight: 'Malam Ganjil',
    blessedNight: 'Malam Berkah',
    hours: 'jam',
    minutes: 'mnt',
    seconds: 'dtk',
    ramadanDay: 'Hari Ramadan',
    lastTenNights: '10 Malam Terakhir',
    searchForNight: 'Cari Lailatul Qadr',
    quranReminder: 'Luangkan waktu dengan Al-Quran hari ini',
    quranSource: 'Baca, hafal, renungkan',
    ramadanInfo: 'Info Ramadan',
    imsakTitle: 'IMSAK & RAMADAN',
    imsakInfo: 'Imsak adalah waktu ketika banyak Muslim berhenti makan sebagai kehati-hatian (sekitar 10 menit sebelum Subuh). Ada perbedaan pendapat ulama: Sebagian menggunakan imsak sebagai batas aman, sebagian lain membolehkan makan hingga waktu Subuh yang sebenarnya.',
    iftarDuaTitle: 'DOA BERBUKA PUASA',
    iftarDuaArabic: 'اللَّهُمَّ لَكَ صُمْتُ وَعَلَى رِزْقِكَ أَفْطَرْتُ',
    iftarDuaTranslit: 'Allahumma laka sumtu wa \'ala rizqika aftartu',
    iftarDuaInfo: 'Doa ini dibaca saat berbuka puasa (Iftar). Kita bersyukur kepada Allah atas nikmat dan kekuatan yang diberikan untuk menyelesaikan puasa.'
  },
  tr: {
    iftarIn: 'Akşama',
    iftarNow: 'Akşam Vakti!',
    fajrUntil: 'İmsaka',
    fajrEnded: 'İmsak oldu',
    selectCity: 'Şehir seç',
    detectLocation: 'Konumu bul',
    locationDetected: 'Konum bulundu',
    imsak: 'İmsak',
    fajr: 'Sabah',
    maghrib: 'Akşam',
    laylatulQadr: 'Kadir Gecesi',
    oddNight: 'Tek Gece',
    blessedNight: 'Mübarek Gece',
    hours: 'saat',
    minutes: 'dk',
    seconds: 'sn',
    ramadanDay: 'Ramazan Günü',
    lastTenNights: 'Son 10 Gece',
    searchForNight: 'Kadir Gecesini ara',
    quranReminder: 'Bugün Kur\'an ile vakit geçir',
    quranSource: 'Oku, ezberle, düşün',
    ramadanInfo: 'Ramazan Bilgisi',
    imsakTitle: 'İMSAK & RAMAZAN',
    imsakInfo: 'İmsak, birçok Müslümanın ihtiyaten yemeği bıraktığı zamandır (sabah namazından yaklaşık 10 dakika önce). Farklı alim görüşleri vardır: Bazıları imsaki güvenlik sınırı olarak kullanırken, diğerleri sabah namazının gerçek başlangıcına kadar yemeye izin verir.',
    iftarDuaTitle: 'İFTAR DUASI',
    iftarDuaArabic: 'اللَّهُمَّ لَكَ صُمْتُ وَعَلَى رِزْقِكَ أَفْطَرْتُ',
    iftarDuaTranslit: 'Allahümme leke sumtu ve \'ala rizkike eftartu',
    iftarDuaInfo: 'Bu dua iftar (oruç açma) sırasında okunur. Allah\'a nimetleri ve orucu tamamlamamız için verdiği güç için şükrederiz.'
  }
};

/**
 * Iftar Countdown Component
 */
const IftarCountdown = ({ language = 'de', ramadanDay = 1, onAfterIftarChange }) => {
  const [location, setLocation] = useState(null);
  const [cityName, setCityName] = useState(null);
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0, isOver: false });
  const [showCitySelector, setShowCitySelector] = useState(false);
  const [isFajrTime, setIsFajrTime] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timeSource, setTimeSource] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  
  const t = RAMADAN_TRANSLATIONS[language] || RAMADAN_TRANSLATIONS.de;

  // Show Ramadan info modal
  const showRamadanInfo = useCallback(() => {
    setShowInfoModal(true);
  }, []);

  // Load saved location
  useEffect(() => {
    const saved = localStorage.getItem('goodDeeds_prayerLocation');
    if (saved) {
      const parsed = JSON.parse(saved);
      setLocation(parsed.coords);
      setCityName(parsed.name);
    }
  }, []);

  // Calculate prayer times when location changes (async with API)
  useEffect(() => {
    if (!location) return;
    
    let isMounted = true;
    
    const fetchTimes = async () => {
      setIsLoading(true);
      
      // First, show local calculation immediately
      const localTimes = calculatePrayerTimesSync(new Date(), location.lat, location.lng);
      if (isMounted) {
        setPrayerTimes(localTimes);
        setTimeSource(localTimes.source || 'local');
      }
      
      // Then fetch from API for accurate times
      try {
        const apiTimes = await calculatePrayerTimes(new Date(), location.lat, location.lng);
        if (isMounted) {
          setPrayerTimes(apiTimes);
          setTimeSource(apiTimes.source || 'api');
        }
      } catch (error) {
        console.warn('API fetch failed, using local times');
      }
      
      if (isMounted) {
        setIsLoading(false);
      }
    };
    
    fetchTimes();
    
    return () => {
      isMounted = false;
    };
  }, [location]);

  // Update countdown every second
  useEffect(() => {
    if (!prayerTimes || !location) return;

    const updateCountdown = async () => {
      const now = new Date();
      
      // Determine if it's before Fajr or after (waiting for Maghrib)
      if (now < prayerTimes.fajr) {
        // Before Fajr - show Fajr countdown
        setIsFajrTime(true);
        setCountdown(getCountdown(prayerTimes.fajr));
        onAfterIftarChange?.(false);
      } else if (now < prayerTimes.maghrib) {
        // After Fajr, before Maghrib - show Maghrib countdown
        setIsFajrTime(false);
        setCountdown(getCountdown(prayerTimes.maghrib));
        onAfterIftarChange?.(false);
      } else {
        // After Maghrib - Iftar time!
        onAfterIftarChange?.(true);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        try {
          const tomorrowTimes = await calculatePrayerTimes(tomorrow, location.lat, location.lng);
          setIsFajrTime(true);
          setCountdown(getCountdown(tomorrowTimes.fajr));
        } catch (error) {
          const tomorrowTimesSync = calculatePrayerTimesSync(tomorrow, location.lat, location.lng);
          setIsFajrTime(true);
          setCountdown(getCountdown(tomorrowTimesSync.fajr));
        }
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [prayerTimes, location, onAfterIftarChange]);

  // Handle city selection
  const selectCity = useCallback((cityKey) => {
    const city = POPULAR_CITIES[cityKey];
    if (city) {
      const coords = { lat: city.lat, lng: city.lng };
      setLocation(coords);
      setCityName(city.name);
      localStorage.setItem('goodDeeds_prayerLocation', JSON.stringify({
        coords,
        name: city.name
      }));
    }
    setShowCitySelector(false);
  }, []);

  // Detect user location
  const detectLocation = useCallback(async () => {
    try {
      const coords = await getUserLocation();
      setLocation(coords);
      setCityName(t.locationDetected);
      localStorage.setItem('goodDeeds_prayerLocation', JSON.stringify({
        coords,
        name: t.locationDetected
      }));
    } catch (error) {
      console.error('Location error:', error);
      selectCity('berlin');
    }
    setShowCitySelector(false);
  }, [selectCity, t.locationDetected]);

  // Check for Laylatul Qadr
  const isLaylatul = isLaylatulQadr(ramadanDay);
  const isOdd = isOddNight(ramadanDay);

  // Calculate Imsak time (10 minutes before Fajr)
  const getImsakTime = () => {
    if (!prayerTimes?.fajr) return null;
    return new Date(prayerTimes.fajr.getTime() - 10 * 60 * 1000);
  };

  return (
    <div className={`iftar-countdown-container ${isLaylatul ? 'laylatul-qadr' : ''} ${isOdd ? 'odd-night' : ''}`}>
      {/* Laylatul Qadr Badge */}
      {isLaylatul && (
        <div className="laylatul-badge">
          <Moon size={14} />
          <span>{isOdd ? t.oddNight : t.lastTenNights}</span>
        </div>
      )}
      
      {/* Location Selector - CENTERED */}
      <div className="location-selector-centered">
        <button 
          className="location-btn"
          onClick={() => setShowCitySelector(!showCitySelector)}
          data-testid="location-selector-btn"
        >
          <MapPin size={14} />
          <span>{cityName || t.selectCity}</span>
          <ChevronDown size={14} className={showCitySelector ? 'rotated' : ''} />
        </button>
        
        {/* Info Button */}
        <button 
          className="info-btn"
          onClick={showRamadanInfo}
          data-testid="ramadan-info-btn"
          title={t.ramadanInfo}
        >
          <Info size={14} />
        </button>
        
        {showCitySelector && (
          <div className="city-dropdown" data-testid="city-dropdown">
            <button 
              className="detect-location-btn"
              onClick={detectLocation}
            >
              <MapPin size={14} />
              {t.detectLocation}
            </button>
            <div className="city-list">
              {Object.entries(POPULAR_CITIES).map(([key, city]) => (
                <button
                  key={key}
                  className="city-option"
                  onClick={() => selectCity(key)}
                >
                  {city.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Main Countdown Display */}
      {prayerTimes && (
        <div className="countdown-display-container">
          <div className="countdown-label-row">
            {isFajrTime ? (
              <>
                <Sunrise size={16} />
                <span>{countdown.isOver ? t.fajrEnded : t.fajrUntil}</span>
              </>
            ) : (
              <>
                <Sunset size={16} />
                <span>{countdown.isOver ? t.iftarNow : t.iftarIn}</span>
              </>
            )}
          </div>
          
          {!countdown.isOver ? (
            <div className="countdown-numbers">
              <div className="countdown-unit">
                <span className="countdown-value">{String(countdown.hours).padStart(2, '0')}</span>
                <span className="countdown-unit-label">{t.hours}</span>
              </div>
              <span className="countdown-separator">:</span>
              <div className="countdown-unit">
                <span className="countdown-value">{String(countdown.minutes).padStart(2, '0')}</span>
                <span className="countdown-unit-label">{t.minutes}</span>
              </div>
              <span className="countdown-separator">:</span>
              <div className="countdown-unit">
                <span className="countdown-value">{String(countdown.seconds).padStart(2, '0')}</span>
                <span className="countdown-unit-label">{t.seconds}</span>
              </div>
            </div>
          ) : (
            <div className="countdown-completed">
              {isFajrTime ? '🌅' : '🌙'}
            </div>
          )}

          {/* Prayer Times Mini Display - Imsak, Fajr, Maghrib */}
          <div className="prayer-times-mini three-times">
            <div className="prayer-time-item">
              <Clock size={12} />
              <span>{t.imsak}: {formatPrayerTime(getImsakTime())}</span>
            </div>
            <div className="prayer-time-item">
              <Sunrise size={12} />
              <span>{t.fajr}: {formatPrayerTime(prayerTimes.fajr)}</span>
            </div>
            <div className="prayer-time-item">
              <Sunset size={12} />
              <span>{t.maghrib}: {formatPrayerTime(prayerTimes.maghrib)}</span>
            </div>
          </div>
        </div>
      )}

      {/* No location selected */}
      {!prayerTimes && (
        <div className="no-location">
          <MapPin size={24} />
          <p>{t.selectCity}</p>
        </div>
      )}

      {/* Ramadan Info Modal */}
      {showInfoModal && (
        <div className="ramadan-info-overlay" onClick={() => setShowInfoModal(false)} data-testid="ramadan-info-modal">
          <div className="ramadan-info-modal" onClick={e => e.stopPropagation()}>
            <button className="ramadan-info-close" onClick={() => setShowInfoModal(false)} data-testid="ramadan-info-close">
              <X size={18} />
            </button>
            <div className="info-section">
              <h4>{t.imsakTitle}</h4>
              <p>{t.imsakInfo}</p>
            </div>
            <div className="info-section dua-section">
              <h4>{t.iftarDuaTitle}</h4>
              <p className="arabic-text">{t.iftarDuaArabic}</p>
              <p className="translit-text">{t.iftarDuaTranslit}</p>
              <p className="dua-info">{t.iftarDuaInfo}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IftarCountdown;
